import { useEffect } from "react";

type SeoHeadProps = {
  title: string;
  description: string;
  robots?: string;
  canonicalPath?: string;
  ogType?: string;
  imagePath?: string;
  structuredData?: Record<string, unknown>;
};

const DEFAULT_SITE_NAME = "RD Trade";
const DEFAULT_ORIGIN = "https://rdtrade.lv";
const DEFAULT_IMAGE_PATH = "/logo-rdtrade-512.png";

const upsertMeta = (selector: string, attributes: Record<string, string>) => {
  let element = document.head.querySelector(selector) as HTMLMetaElement | null;

  if (!element) {
    element = document.createElement("meta");
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    element?.setAttribute(key, value);
  });
};

const upsertLink = (selector: string, attributes: Record<string, string>) => {
  let element = document.head.querySelector(selector) as HTMLLinkElement | null;

  if (!element) {
    element = document.createElement("link");
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    element?.setAttribute(key, value);
  });
};

const upsertStructuredData = (structuredData?: Record<string, unknown>) => {
  const selector = 'script[data-seo-structured="true"]';
  const existing = document.head.querySelector(selector);

  if (!structuredData) {
    existing?.remove();
    return;
  }

  const script = existing ?? document.createElement("script");
  script.setAttribute("type", "application/ld+json");
  script.setAttribute("data-seo-structured", "true");
  script.textContent = JSON.stringify(structuredData);

  if (!existing) {
    document.head.appendChild(script);
  }
};

export const SeoHead = ({
  title,
  description,
  robots = "index,follow",
  canonicalPath,
  ogType = "website",
  imagePath = DEFAULT_IMAGE_PATH,
  structuredData,
}: SeoHeadProps) => {
  useEffect(() => {
    const origin = window.location.origin.startsWith("http")
      ? window.location.origin
      : DEFAULT_ORIGIN;
    const canonicalUrl = canonicalPath
      ? new URL(canonicalPath, origin).toString()
      : window.location.href;
    const imageUrl = new URL(imagePath, origin).toString();

    document.title = title;

    upsertMeta('meta[name="description"]', {
      name: "description",
      content: description,
    });
    upsertMeta('meta[name="robots"]', {
      name: "robots",
      content: robots,
    });
    upsertMeta('meta[property="og:site_name"]', {
      property: "og:site_name",
      content: DEFAULT_SITE_NAME,
    });
    upsertMeta('meta[property="og:type"]', {
      property: "og:type",
      content: ogType,
    });
    upsertMeta('meta[property="og:title"]', {
      property: "og:title",
      content: title,
    });
    upsertMeta('meta[property="og:description"]', {
      property: "og:description",
      content: description,
    });
    upsertMeta('meta[property="og:url"]', {
      property: "og:url",
      content: canonicalUrl,
    });
    upsertMeta('meta[property="og:image"]', {
      property: "og:image",
      content: imageUrl,
    });
    upsertMeta('meta[name="twitter:card"]', {
      name: "twitter:card",
      content: "summary_large_image",
    });
    upsertMeta('meta[name="twitter:title"]', {
      name: "twitter:title",
      content: title,
    });
    upsertMeta('meta[name="twitter:description"]', {
      name: "twitter:description",
      content: description,
    });
    upsertMeta('meta[name="twitter:image"]', {
      name: "twitter:image",
      content: imageUrl,
    });
    upsertLink('link[rel="canonical"]', {
      rel: "canonical",
      href: canonicalUrl,
    });
    upsertStructuredData(structuredData);
  }, [canonicalPath, description, imagePath, ogType, robots, structuredData, title]);

  return null;
};
